import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import {
  Loader2, Play, ChevronRight, CheckCircle2, AlertTriangle, Users,
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ScatterChart, Scatter, ZAxis, Cell, ComposedChart, Area, Line, ReferenceLine,
} from 'recharts'

/**
 * Manager selection — four decisions over the hedge fund universe.
 *
 * The sibling Strategy Lab handles systematic strategies, which are transparent, daily
 * and cheap. A hedge fund is none of those things: it publishes one number a month and
 * charges for the privilege, so the questions are different. Who are its real peers,
 * given the strategy labels disagree across vendors? Is it good against them? Is it
 * good against the factors you could buy instead? And is its reported volatility real?
 *
 * Every stage states what it rests on, because on monthly data most of these numbers
 * are weaker than they look.
 */

const th = 'px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap'
const td = 'px-3 py-2 text-sm text-gray-800 whitespace-nowrap'
const inp = 'rounded-md border border-gray-300 px-2 py-1 text-sm'
const btnPrimary = 'flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50'

const num = (v: any, d = 2) => (v == null || Number.isNaN(v) ? '—' : Number(v).toFixed(d))
const pct = (v: any, d = 1) => (v == null || Number.isNaN(v) ? '—' : `${(Number(v) * 100).toFixed(d)}%`)

const STAGES = ['Shortlist', 'Peer groups', 'Peer-relative', 'Benchmark', 'Portfolio fit'] as const
const CLUSTER_COLOURS = ['#1F4E79', '#2F6B4F', '#8A6A1F', '#7A3B69', '#3E6E8E', '#96502F']

export default function ManagerSelection() {
  const [stage, setStage] = useState<string>('Shortlist')
  const [source, setSource] = useState('albourne')
  const [minMonths, setMinMonths] = useState(120)
  const [limit, setLimit] = useState(120)
  const [fundId, setFundId] = useState('')
  const [altWeight, setAltWeight] = useState(0.10)
  const [dominant, setDominant] = useState('equity')
  const [stressQ, setStressQ] = useState(0.25)
  const [retViews, setRetViews] = useState<Record<string, number | undefined>>({})
  const [volViews, setVolViews] = useState<Record<string, number | undefined>>({})
  const [confidence, setConfidence] = useState(0.75)
  const [corrStrength, setCorrStrength] = useState(0)
  const [minSharpe, setMinSharpe] = useState(0.8)
  const [maxCvar, setMaxCvar] = useState(0.08)
  const [betaLow, setBetaLow] = useState(0.7)
  const [betaHigh, setBetaHigh] = useState(1.3)
  const [status, setStatus] = useState('')

  const factors = useQuery({
    queryKey: ['ms-factors'],
    queryFn: () => api.get('/manager-selection/factors').then(r => r.data),
  })

  const clusters = useMutation({
    mutationFn: () => api.post('/manager-selection/clusters', {
      source, min_months: minMonths, limit, start: '2010-01-01',
    }).then(r => r.data),
    onSuccess: (d) => setStatus(d.available
      ? `${d.groups?.n_clusters} groups over ${d.groups?.n_funds} funds`
      : d.reason),
    onError: (e: any) => setStatus(e?.response?.data?.detail || 'Clustering failed'),
  })

  const peers = useMutation({
    mutationFn: () => api.post('/manager-selection/peers', {
      fund_id: fundId, source, min_months: minMonths, limit, start: '2010-01-01',
    }).then(r => r.data),
    onSuccess: (d) => setStatus(d.available ? `${d.n_peers} peers` : d.reason),
    onError: (e: any) => setStatus(e?.response?.data?.detail || 'Peer analysis failed'),
  })

  const bench = useMutation({
    mutationFn: () => api.post('/manager-selection/benchmark', {
      fund_id: fundId, source, start: '2010-01-01', max_factors: 6,
    }).then(r => r.data),
    onSuccess: (d) => setStatus(d.available ? 'Replica fitted' : d.reason),
    onError: (e: any) => setStatus(e?.response?.data?.detail || 'Benchmark analysis failed'),
  })

  const book = useQuery({
    queryKey: ['ms-book'],
    queryFn: () => api.get('/manager-selection/book').then(r => r.data),
    enabled: stage === 'Portfolio fit',
    staleTime: Infinity,
  })

  // Views are sent only where a number was entered. An empty box means "no opinion",
  // which is not the same as a view of zero -- sending 0 would assert the sleeve returns
  // nothing, and Black-Litterman would dutifully believe it.
  const clean = (m: Record<string, number | undefined>) => {
    const out: Record<string, number> = {}
    for (const [k, v] of Object.entries(m)) {
      if (v !== undefined && v !== null && !Number.isNaN(v)) out[k] = v
    }
    return Object.keys(out).length ? out : null
  }

  const fit = useMutation({
    mutationFn: () => api.post('/manager-selection/candidate-fit', {
      fund_id: fundId, source, start: '2010-01-01',
      alt_weight: altWeight, dominant, stress_quantile: stressQ,
    }).then(r => r.data),
    onSuccess: (d) => setStatus(d.available ? `Fit tested over ${d.n_months} months` : d.reason),
    onError: (e: any) => setStatus(e?.response?.data?.detail || 'Fit test failed'),
  })

  const scen = useMutation({
    mutationFn: () => api.post('/manager-selection/book-scenario', {
      fund_id: fundId || null, source, start: '2010-01-01', alt_weight: altWeight,
      return_views: clean(retViews), volatility_views: clean(volViews),
      confidence, correlation_strength: corrStrength,
    }).then(r => r.data),
    onSuccess: (d) => setStatus(d.available ? 'Scenario applied' : d.reason),
    onError: (e: any) => setStatus(e?.response?.data?.detail || 'Scenario failed'),
  })

  const scenarioSleeves: string[] = (book.data?.sleeves || [])
    .concat(fundId ? ['alternatives'] : [])

  const screen = useMutation({
    mutationFn: () => api.post('/manager-selection/screen', {
      source, min_months: minMonths, limit, min_sharpe: minSharpe,
      max_cvar: maxCvar, start: '2010-01-01',
    }).then(r => r.data),
    onSuccess: (d) => setStatus(d.available
      ? `${d.n_passed} of ${d.n_scored} funds passed` : d.reason),
    onError: (e: any) => setStatus(e?.response?.data?.detail || 'Screening failed'),
  })

  const similar = useMutation({
    mutationFn: () => api.post('/manager-selection/similar', {
      fund_id: fundId, source, min_months: minMonths, limit,
      beta_low: betaLow, beta_high: betaHigh, start: '2010-01-01',
    }).then(r => r.data),
    onSuccess: (d) => setStatus(d.available
      ? `${d.n_comparable} comparable of ${d.n_scored} scored` : d.reason),
    onError: (e: any) => setStatus(e?.response?.data?.detail || 'Similarity search failed'),
  })

  const assignments: any[] = clusters.data?.assignments || []
  const done: Record<string, boolean> = {
    'Shortlist': !!screen.data?.available || !!similar.data?.available,
    'Peer groups': !!clusters.data?.available,
    'Peer-relative': !!peers.data?.available,
    'Benchmark': !!bench.data?.available,
    'Portfolio fit': !!fit.data?.available,
  }

  const smoothing = bench.data?.smoothing

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Manager Selection</h1>
          <p className="max-w-3xl text-sm text-gray-500">
            Four decisions over the hedge fund universe: who a fund's real peers are, how it
            stands against them, what is left after the factors you could buy instead, and
            whether it helps the book when it is supposed to.
          </p>
        </div>
        {status && <div className="rounded bg-gray-100 px-3 py-1.5 text-xs text-gray-600">{status}</div>}
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-1 border-b border-gray-200">
        {STAGES.map((s, i) => (
          <div key={s} className="flex items-center">
            <button onClick={() => setStage(s)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm ${
                stage === s ? 'border-b-2 border-blue-600 font-medium text-blue-700'
                            : 'text-gray-600 hover:text-gray-900'}`}>
              {done[s] && <CheckCircle2 size={14} className="text-emerald-600" />}
              <span className="text-xs text-gray-400">{i + 1}</span>{s}
            </button>
            {i < STAGES.length - 1 && <ChevronRight size={14} className="text-gray-300" />}
          </div>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
        <label className="text-sm">Vendor
          <select className={`${inp} ml-1`} value={source} onChange={e => setSource(e.target.value)}>
            <option value="albourne">Albourne</option>
            <option value="hfr">HFR</option>
            <option value="rgg">RGG (house)</option>
          </select>
        </label>
        <label className="text-sm">Min months <input className={`${inp} ml-1 w-20`} type="number"
          value={minMonths} onChange={e => setMinMonths(+e.target.value)} /></label>
        <label className="text-sm">Universe size <input className={`${inp} ml-1 w-20`} type="number"
          value={limit} onChange={e => setLimit(+e.target.value)} /></label>
        {factors.data?.n && (
          <span className="text-xs text-gray-500">{factors.data.n} house factor cells</span>
        )}
      </div>

      {/* --------------------------------------------------------- 0. shortlist */}
      {stage === 'Shortlist' && (
        <div className="space-y-4">
          <Note>
            Two ways into a universe of thousands. Screen by the mandate's own constraints,
            or anchor on a fund that already does what you want and ask who resembles it.
            Both are shortlisting — neither judges a fund, they decide what it will be
            judged against.
          </Note>

          <Panel title="Screen by mandate constraints">
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm">Min Sharpe <input className={`${inp} ml-1 w-20`}
                type="number" step="0.1" value={minSharpe}
                onChange={e => setMinSharpe(+e.target.value)} /></label>
              <label className="text-sm">Max CVaR 95% <input className={`${inp} ml-1 w-20`}
                type="number" step="0.01" value={maxCvar}
                onChange={e => setMaxCvar(+e.target.value)} /></label>
              <button className={btnPrimary} disabled={screen.isPending} onClick={() => screen.mutate()}>
                {screen.isPending ? <Loader2 className="animate-spin" size={15} /> : <Play size={15} />}
                Screen
              </button>
            </div>

            {screen.isPending && <div className="mt-3"><Spinner label="Scoring the universe…" /></div>}
            {screen.data && !screen.data.available && <div className="mt-3"><Note tone="warn">{screen.data.reason}</Note></div>}

            {screen.data?.available && (
              <>
                <div className="mt-3 grid gap-3 sm:grid-cols-3">
                  <Kpi label="Scored" value={screen.data.n_scored} />
                  <Kpi label="Passed" value={screen.data.n_passed} />
                  <Kpi label="Pass rate" value={pct(screen.data.pass_rate, 0)} />
                </div>
                <div className="mt-3 text-xs text-gray-600">
                  Rejected by: {Object.entries(screen.data.rejected_by || {})
                    .map(([k, v]: any) => `${k} ${v}`).join(' · ') || 'nothing'}
                </div>
                <div className="mt-2 text-xs text-gray-500">{screen.data.note}</div>
                <div className="mt-3 max-h-80 overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="sticky top-0 bg-white">
                      <tr>{['Fund', 'Sharpe', 'Ann. return', 'Ann. vol', 'CVaR 95%',
                            'Max DD', ''].map(h => <th key={h} className={th}>{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(screen.data.rows || []).slice(0, 60).map((r: any) => (
                        <tr key={r.fund} className="hover:bg-gray-50">
                          <td className={td}>{r.name}</td>
                          <td className={`${td} font-medium`}>{num(r.sharpe)}</td>
                          <td className={td}>{pct(r.ann_return)}</td>
                          <td className={td}>{pct(r.ann_vol)}</td>
                          <td className={td}>{pct(r.cvar_95)}</td>
                          <td className={td}>{pct(r.max_drawdown)}</td>
                          <td className={td}>
                            <button className="rounded border border-gray-300 px-2 py-0.5 text-xs hover:bg-gray-50"
                              onClick={() => setFundId(r.fund)}>Anchor</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Panel>

          <Panel title="Find funds like this one">
            <div className="flex flex-wrap items-end gap-3">
              <span className="text-sm text-gray-600">Anchor: {fundId || <em>none selected</em>}</span>
              <label className="text-sm">Beta from <input className={`${inp} ml-1 w-16`}
                type="number" step="0.1" value={betaLow}
                onChange={e => setBetaLow(+e.target.value)} /></label>
              <label className="text-sm">to <input className={`${inp} ml-1 w-16`}
                type="number" step="0.1" value={betaHigh}
                onChange={e => setBetaHigh(+e.target.value)} /></label>
              <button className={btnPrimary} disabled={!fundId || similar.isPending}
                onClick={() => similar.mutate()}>
                {similar.isPending ? <Loader2 className="animate-spin" size={15} /> : <Users size={15} />}
                Find similar
              </button>
            </div>

            {similar.isPending && <div className="mt-3"><Spinner label="Regressing candidates on the anchor…" /></div>}
            {similar.data && !similar.data.available && <div className="mt-3"><Note tone="warn">{similar.data.reason}</Note></div>}

            {similar.data?.available && (
              <>
                <div className="mt-3"><Note>{similar.data.note}</Note></div>
                <div className="mt-3 max-h-80 overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="sticky top-0 bg-white">
                      <tr>{['Fund', 'R²', 'Beta', 'Correlation', 'Alpha p.a.',
                            'Tracking error', 'Months', ''].map(h =>
                        <th key={h} className={th}>{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(similar.data.all || []).slice(0, 60).map((r: any) => {
                        const inBand = r.beta >= betaLow && r.beta <= betaHigh
                        return (
                          <tr key={r.fund} className={inBand ? 'hover:bg-gray-50' : 'text-gray-400'}>
                            <td className={td}>{r.name}</td>
                            <td className={`${td} font-medium`}>{num(r.r_squared, 3)}</td>
                            <td className={td} title={inBand ? undefined : 'outside the comparable-size band'}>
                              {num(r.beta)}{inBand ? '' : ' ⚠'}
                            </td>
                            <td className={td}>{num(r.correlation)}</td>
                            <td className={td}>{pct(r.alpha_annual)}</td>
                            <td className={td}>{pct(r.tracking_error)}</td>
                            <td className={td}>{r.n_overlap}</td>
                            <td className={td}>
                              <button className="rounded border border-gray-300 px-2 py-0.5 text-xs hover:bg-gray-50"
                                onClick={() => { setFundId(r.fund); setStage('Peer-relative') }}>
                                Analyse
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  Greyed rows resemble the anchor but run at a different size. High R² with a
                  beta of 0.3 is a diluted version of the anchor, not a peer for it.
                </div>
              </>
            )}
          </Panel>
        </div>
      )}

      {/* ------------------------------------------------------ 1. peer groups */}
      {stage === 'Peer groups' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <button className={btnPrimary} disabled={clusters.isPending} onClick={() => clusters.mutate()}>
              {clusters.isPending ? <Loader2 className="animate-spin" size={15} /> : <Play size={15} />}
              Build peer groups
            </button>
          </div>
          {clusters.isPending && <Spinner label="Clustering on return behaviour…" />}
          {clusters.data && !clusters.data.available && <Note tone="warn">{clusters.data.reason}</Note>}

          {clusters.data?.available && (
            <>
              <Note>{clusters.data.note}</Note>
              {clusters.data.warning && <Note tone="warn">{clusters.data.warning}</Note>}

              <div className="grid gap-3 sm:grid-cols-4">
                <Kpi label="Groups" value={clusters.data.groups?.n_clusters} />
                <Kpi label="Unassigned" value={clusters.data.groups?.n_unassigned} />
                <Kpi label="Within-group corr" value={num(clusters.data.groups?.within_corr)} />
                <Kpi label="Between-group corr" value={num(clusters.data.groups?.between_corr)} />
              </div>

              {clusters.data.map?.length > 0 && (
                <Panel title="The universe, mapped by return behaviour">
                  <ResponsiveContainer width="100%" height={340}>
                    <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" dataKey="x" tick={{ fontSize: 10 }} />
                      <YAxis type="number" dataKey="y" tick={{ fontSize: 10 }} />
                      <ZAxis range={[40, 40]} />
                      <Tooltip content={({ payload }: any) => payload?.[0] ? (
                        <div className="rounded border bg-white px-2 py-1 text-xs shadow">
                          <div className="font-medium">{payload[0].payload.name}</div>
                          <div>cluster {payload[0].payload.cluster}</div>
                        </div>) : null} />
                      <Scatter data={clusters.data.map}>
                        {clusters.data.map.map((p: any, i: number) => (
                          <Cell key={i} fill={p.cluster < 0 ? '#C9CED4'
                            : CLUSTER_COLOURS[p.cluster % CLUSTER_COLOURS.length]} />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                  <div className="mt-1 text-xs text-gray-500">
                    Distances are correlation distances; grey points had no group large enough
                    to join. The map and the grouping come from the same matrix, so they agree.
                  </div>
                </Panel>
              )}

              <Panel title="What each group actually is">
                <div className="max-h-80 overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="sticky top-0 bg-white">
                      <tr>{['Group', 'Funds', 'Ann. return', 'Ann. vol', 'Sharpe',
                            'Mean corr', 'Most common label', 'Distinct labels'].map(h =>
                        <th key={h} className={th}>{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(clusters.data.profile || []).map((p: any) => (
                        <tr key={p.cluster} className="hover:bg-gray-50">
                          <td className={td}>
                            <span className="inline-block h-2 w-2 rounded-full"
                              style={{ background: CLUSTER_COLOURS[p.cluster % CLUSTER_COLOURS.length] }} />
                            <span className="ml-2">{p.cluster}</span>
                          </td>
                          <td className={td}>{p.n_funds}</td>
                          <td className={td}>{pct(p.ann_return)}</td>
                          <td className={td}>{pct(p.ann_vol)}</td>
                          <td className={`${td} font-medium`}>{num(p.sharpe)}</td>
                          <td className={td}>{num(p.mean_pairwise_corr)}</td>
                          <td className={td}>{p.top_main_strategy || '—'}</td>
                          <td className={td} title="How many different vendor labels this behavioural group spans">
                            {p.n_labels_main_strategy ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  A group spanning many distinct labels is the finding: the labels were not
                  describing behaviour. A group dominated by one label is a case where they were.
                </div>
              </Panel>

              <Panel title="Pick a fund to analyse">
                <div className="max-h-80 overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="sticky top-0 bg-white">
                      <tr>{['Fund', 'Group', ''].map((h, i) => <th key={i} className={th}>{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {assignments.slice(0, 200).map((a: any) => (
                        <tr key={a.fund_id} className={fundId === a.fund_id ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                          <td className={td}>{a.name}</td>
                          <td className={td}>{a.cluster < 0 ? 'none' : a.cluster}</td>
                          <td className={td}>
                            <button className="rounded border border-gray-300 px-2 py-0.5 text-xs hover:bg-gray-50"
                              onClick={() => { setFundId(a.fund_id); setStage('Peer-relative') }}>
                              Select
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* --------------------------------------------------- 2. peer-relative */}
      {stage === 'Peer-relative' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-gray-600">Fund: {fundId || <em>none selected</em>}</span>
            <button className={btnPrimary} disabled={!fundId || peers.isPending} onClick={() => peers.mutate()}>
              {peers.isPending ? <Loader2 className="animate-spin" size={15} /> : <Users size={15} />}
              Compare with peers
            </button>
          </div>
          {peers.isPending && <Spinner label="Placing the fund in its peer distribution…" />}
          {peers.data && !peers.data.available && <Note tone="warn">{peers.data.reason}</Note>}

          {peers.data?.available && (
            <>
              <Note>{peers.data.note}</Note>
              <Panel title={`${peers.data.fund?.name} — standing against ${peers.data.n_peers} peers`}>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={Object.entries(peers.data.standing?.percentiles || {})
                    .filter(([, v]: any) => v != null)
                    .map(([k, v]: any) => ({ metric: k, pctile: v }))}
                    margin={{ top: 10, right: 20, bottom: 40, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="metric" angle={-30} textAnchor="end" height={70} tick={{ fontSize: 10 }} />
                    <YAxis domain={[0, 1]} tick={{ fontSize: 10 }} tickFormatter={(v: any) => pct(v, 0)} />
                    <Tooltip formatter={(v: any) => pct(v, 0)} />
                    <ReferenceLine y={0.5} stroke="#8A9BA8" strokeDasharray="4 3" />
                    <Bar dataKey="pctile">
                      {Object.entries(peers.data.standing?.percentiles || {}).map(([, v]: any, i: number) => (
                        <Cell key={i} fill={v > 0.66 ? '#2F6B4F' : v < 0.33 ? '#96502F' : '#8A6A1F'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-1 text-xs text-gray-500">
                  Direction-adjusted, so higher is always better — including for drawdown and
                  for serial correlation, where less is the more honest answer.
                </div>
              </Panel>

              {(peers.data.factor_table || []).length > 0 && (
                <Panel title="Exposures and residual, on the same factors across the group">
                  <div className="max-h-80 overflow-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="sticky top-0 bg-white">
                        <tr>{Object.keys(peers.data.factor_table[0]).map(h =>
                          <th key={h} className={th}>{h === 'fund' ? 'Fund' : h}</th>)}</tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {peers.data.factor_table.slice(0, 40).map((r: any, i: number) => (
                          <tr key={i} className={r.fund === fundId ? 'bg-blue-50 font-medium' : 'hover:bg-gray-50'}>
                            {Object.entries(r).map(([k, v]: any, j: number) => (
                              <td key={j} className={td}>
                                {k === 'fund' ? (peers.data.names?.[v] || v)
                                  : typeof v === 'number' ? num(v, 3) : String(v ?? '—')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Panel>
              )}

              {(peers.data.rolling_beta || []).length > 0 && (
                <Panel title={`Rolling beta against peer quartiles — ${peers.data.rolling_beta[0]?.factor}`}>
                  <ResponsiveContainer width="100%" height={260}>
                    <ComposedChart data={peers.data.rolling_beta} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={40} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v: any) => num(v, 2)} />
                      <Area type="monotone" dataKey="peer_q3" stroke="none" fill="#D8E3EE" />
                      <Area type="monotone" dataKey="peer_q1" stroke="none" fill="#FFFFFF" />
                      <Line type="monotone" dataKey="peer_median" stroke="#8A9BA8" dot={false} strokeDasharray="4 3" />
                      <Line type="monotone" dataKey="fund_beta" stroke="#1F4E79" dot={false} strokeWidth={2} />
                    </ComposedChart>
                  </ResponsiveContainer>
                  <div className="mt-1 text-xs text-gray-500">
                    Shaded band is the peer interquartile range. A beta that moves while the band
                    stays put is a decision; one that moves with the band is not.
                  </div>
                </Panel>
              )}
            </>
          )}
        </div>
      )}

      {/* ------------------------------------------------------- 3. benchmark */}
      {stage === 'Benchmark' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-gray-600">Fund: {fundId || <em>none selected</em>}</span>
            <button className={btnPrimary} disabled={!fundId || bench.isPending} onClick={() => bench.mutate()}>
              {bench.isPending ? <Loader2 className="animate-spin" size={15} /> : <Play size={15} />}
              Fit the replica
            </button>
          </div>
          {bench.isPending && <Spinner label="Fitting house factors and building the daily replica…" />}
          {bench.data && !bench.data.available && <Note tone="warn">{bench.data.reason}</Note>}

          {bench.data?.available && (
            <>
              <Note>{bench.data.note}</Note>
              <div className="grid gap-3 sm:grid-cols-4">
                <Kpi label="R²" value={pct(bench.data.fit?.r_squared, 0)} />
                <Kpi label="Residual alpha p.a." value={pct(bench.data.fit?.alpha_annual)} />
                <Kpi label="t(alpha)" value={num(bench.data.fit?.t_alpha)} />
                <Kpi label="Factors used" value={bench.data.fit?.n_selected} />
              </div>

              {smoothing && (
                <Panel title="Is the reported volatility real?">
                  <Note tone={smoothing.both_agree ? 'warn' : undefined}>{smoothing.verdict}</Note>
                  <div className="mt-3 grid gap-3 sm:grid-cols-4">
                    <Kpi label="Reported vol" value={pct(smoothing.reported_vol)} />
                    <Kpi label="Unsmoothed vol" value={pct(smoothing.unsmoothed_vol)} />
                    <Kpi label="Serial correlation" value={num(smoothing.autocorr_1)} />
                    <Kpi label="Exposure-implied vol" value={pct(smoothing.exposure_implied_vol)} />
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    Two independent tests, never blended: serial correlation rests only on the
                    fund's own numbers, the exposure-implied figure only on the daily factors.
                    Agreement between them is what is worth acting on.
                  </div>
                </Panel>
              )}

              <Panel title="Factor weights">
                <div className="max-h-64 overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="sticky top-0 bg-white">
                      <tr>{['Factor', 'Weight'].map(h => <th key={h} className={th}>{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {Object.entries(bench.data.weights || {}).map(([k, v]: any) => (
                        <tr key={k}><td className={td}>{k}</td>
                          <td className={`${td} font-medium`}>{num(v, 3)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>

              {(bench.data.intra_month_events || []).length > 0 && (
                <Panel title="Months the fund's single published number did not describe">
                  <div className="max-h-72 overflow-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="sticky top-0 bg-white">
                        <tr>{['Month', 'Replica intra-month drawdown', 'Replica month end',
                              'Fund reported', 'Days to trough'].map(h =>
                          <th key={h} className={th}>{h}</th>)}</tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {bench.data.intra_month_events.map((e: any, i: number) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className={td}>{e.month}</td>
                            <td className={`${td} font-medium`}>{pct(e.replica_intramonth_drawdown)}</td>
                            <td className={td}>{pct(e.replica_month_return)}</td>
                            <td className={td}>{e.fund_month_return == null ? '—' : pct(e.fund_month_return)}</td>
                            <td className={td}>{e.days_to_trough}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    Where the intra-month drawdown is much deeper than the month-end figure, the
                    episode is invisible in a monthly series — the fund fell and recovered between
                    two published numbers.
                  </div>
                </Panel>
              )}

              {(bench.data.rolling || []).length > 0 && (
                <Panel title="Is the residual persistent, or episodic?">
                  <ResponsiveContainer width="100%" height={240}>
                    <ComposedChart data={bench.data.rolling} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={40} />
                      <YAxis yAxisId="l" tick={{ fontSize: 10 }} tickFormatter={(v: any) => pct(v, 0)} />
                      <YAxis yAxisId="r" orientation="right" domain={[0, 1]} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v: any) => num(v, 3)} />
                      <Line yAxisId="l" type="monotone" dataKey="alpha_annual" stroke="#1F4E79" dot={false} strokeWidth={2} />
                      <Line yAxisId="r" type="monotone" dataKey="r_squared" stroke="#8A6A1F" dot={false} strokeDasharray="4 3" />
                    </ComposedChart>
                  </ResponsiveContainer>
                  <div className="mt-1 text-xs text-gray-500">
                    Solid: rolling residual alpha (left). Dashed: R² (right). A falling R² usually
                    means the implementation changed.
                  </div>
                </Panel>
              )}
            </>
          )}
        </div>
      )}

      {/* ------------------------------------------------------ 4. portfolio fit */}
      {stage === 'Portfolio fit' && (
        <div className="space-y-4">
          <Note>
            The last decision, and the only one that asks about the book rather than the
            fund. A candidate is not judged on its own numbers but on what it adds to an
            allocation somebody already owns — so the test is run twice, once over the
            whole sample and once inside the worst quartile of the dominant sleeve.
            Diversifying on average but not when it is needed is the failure worth catching.
          </Note>

          <Panel title="The book">
            {book.isLoading && <Spinner label="Assembling sleeves from house market data…" />}
            {book.data && !book.data.available && <Note tone="warn">{book.data.reason}</Note>}
            {book.data?.available && (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead><tr>{['Sleeve', 'Instrument', 'Weight without candidate',
                                 'Weight with candidate', 'Return p.a.', 'Vol p.a.'].map(h =>
                      <th key={h} className={th}>{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {book.data.sleeves.map((s: string) => (
                        <tr key={s}>
                          <td className={`${td} font-medium`}>{s}</td>
                          <td className={td}>{book.data.instruments?.[s] || '—'}</td>
                          <td className={td}>{pct(book.data.weights_before?.[s])}</td>
                          <td className={td}>{pct(book.data.weights_after?.[s])}</td>
                          <td className={td}>{pct(book.data.ann_return?.[s])}</td>
                          <td className={td}>{pct(book.data.ann_vol?.[s])}</td>
                        </tr>
                      ))}
                      <tr className="bg-blue-50/50">
                        <td className={`${td} font-medium`}>alternatives</td>
                        <td className={td}>{fundId || <em className="text-gray-400">the candidate</em>}</td>
                        <td className={td}>—</td>
                        <td className={td}>{pct(book.data.weights_after?.alternatives)}</td>
                        <td className={td}>—</td>
                        <td className={td}>—</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  {book.data.n_months} months, {book.data.window?.[0]} to {book.data.window?.[1]}.
                  {' '}{book.data.note}
                </div>
              </>
            )}
          </Panel>

          <Panel title="Does the candidate help?">
            <div className="flex flex-wrap items-end gap-3">
              <span className="text-sm text-gray-600">
                Candidate: {fundId || <em className="text-gray-400">pick one on Shortlist</em>}
              </span>
              <label className="text-sm">Sleeve weight <input className={`${inp} ml-1 w-16`}
                type="number" step="0.05" min="0.01" max="0.45" value={altWeight}
                onChange={e => setAltWeight(+e.target.value)} /></label>
              <label className="text-sm">Dominant
                <select className={`${inp} ml-1`} value={dominant}
                  onChange={e => setDominant(e.target.value)}>
                  {(book.data?.sleeves || ['equity']).map((s: string) =>
                    <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="text-sm">Stress quantile <input className={`${inp} ml-1 w-16`}
                type="number" step="0.05" min="0.05" max="0.45" value={stressQ}
                onChange={e => setStressQ(+e.target.value)} /></label>
              <button className={btnPrimary} disabled={!fundId || fit.isPending}
                onClick={() => fit.mutate()}>
                {fit.isPending ? <Loader2 className="animate-spin" size={15} /> : <Play size={15} />}
                Test fit
              </button>
            </div>

            {fit.isPending && <div className="mt-3"><Spinner label="Recomputing contribution through the covariance…" /></div>}
            {fit.data && !fit.data.available && <div className="mt-3"><Note tone="warn">{fit.data.reason}</Note></div>}

            {fit.data?.available && (
              <>
                <div className="mt-3">
                  <Note tone={/detracts|not when it is needed/.test(fit.data.verdict || '') ? 'warn' : 'info'}>
                    {fit.data.verdict}
                  </Note>
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-4">
                  <Kpi label="Book vol before" value={pct(fit.data.correlation?.portfolio_vol_before)} />
                  <Kpi label="Book vol after" value={pct(fit.data.correlation?.portfolio_vol_after)} />
                  <Kpi label={`Corr to ${fit.data.dominant} before`}
                       value={num(fit.data.correlation?.corr_to_dominant_before)} />
                  <Kpi label={`Corr to ${fit.data.dominant} after`}
                       value={num(fit.data.correlation?.corr_to_dominant_after)} />
                </div>

                {fit.data.correlation?.decorrelated_against_dominant === false && (
                  <div className="mt-3">
                    <Note tone="warn">
                      Adding the candidate did not reduce the book's correlation to{' '}
                      {fit.data.dominant}. Whatever else it brings, it is not
                      diversification against the exposure that dominates this book.
                    </Note>
                  </div>
                )}

                {[['full', 'Whole sample'],
                  ['stressed', `Worst ${pct(stressQ, 0)} of ${fit.data.dominant} months`]].map(([key, title]) => {
                  const p = (fit.data as any)[key]
                  if (!p) return null
                  return (
                    <div key={key} className="mt-4">
                      <div className="mb-1 text-sm font-medium text-gray-700">
                        {title}
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          book {pct(p.portfolio_return)} p.a. at {pct(p.portfolio_vol)} vol
                        </span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead><tr>{['Sleeve', 'Weight', 'Return contribution',
                                       'Vol contribution', 'Return share', 'Vol share',
                                       'Return per unit risk'].map(h =>
                            <th key={h} className={th}>{h}</th>)}</tr></thead>
                          <tbody className="divide-y divide-gray-100">
                            {p.rows.map((r: any) => (
                              <tr key={r.index}
                                  className={r.index === 'alternatives' ? 'bg-blue-50/50' : ''}>
                                <td className={`${td} ${r.index === 'alternatives' ? 'font-medium' : ''}`}>
                                  {r.index === 'alternatives' ? `alternatives (${fundId})` : r.index}
                                </td>
                                <td className={td}>{pct(r.weight)}</td>
                                <td className={td}>{pct(r.return_contribution)}</td>
                                <td className={td}>{pct(r.vol_contribution)}</td>
                                <td className={td}>{pct(r.return_share)}</td>
                                <td className={td}>{pct(r.vol_share)}</td>
                                <td className={`${td} font-medium ${Number(r.return_per_unit_risk) < 0 ? 'text-red-600' : ''}`}>
                                  {num(r.return_per_unit_risk)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )
                })}

                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={(fit.data.full?.rows || []).map((r: any) => ({
                      sleeve: r.index, 'Return share': r.return_share, 'Vol share': r.vol_share,
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="sleeve" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={(v: any) => `${(v * 100).toFixed(0)}%`} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v: any) => pct(v)} />
                      <Bar dataKey="Return share" fill="#2563eb" />
                      <Bar dataKey="Vol share" fill="#f59e0b" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-xs text-gray-500">
                  A sleeve earning its place shows a return share above its volatility
                  share. {fit.data.note}
                </div>
              </>
            )}
          </Panel>

          <Panel title="Forward views">
            <div className="text-xs text-gray-500">
              Two different statements, stated separately because they are different
              claims. A return view is a level — the annual return you expect. A
              volatility view is a change — <code>+0.25</code> means a quarter more
              volatile than history, not 25% volatility. Both are blended at the
              confidence below rather than imposed.
            </div>

            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead><tr>{['Sleeve', 'Return view p.a.', 'Vol change'].map(h =>
                  <th key={h} className={th}>{h}</th>)}</tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {scenarioSleeves.map((s: string) => (
                    <tr key={s}>
                      <td className={`${td} font-medium`}>
                        {s === 'alternatives' && fundId ? `alternatives (${fundId})` : s}
                      </td>
                      <td className={td}>
                        <input className={`${inp} w-24`} type="number" step="0.01"
                          placeholder="—" value={retViews[s] ?? ''}
                          onChange={e => setRetViews({ ...retViews,
                            [s]: e.target.value === '' ? undefined : +e.target.value })} />
                      </td>
                      <td className={td}>
                        <input className={`${inp} w-24`} type="number" step="0.05"
                          placeholder="—" value={volViews[s] ?? ''}
                          onChange={e => setVolViews({ ...volViews,
                            [s]: e.target.value === '' ? undefined : +e.target.value })} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-3 flex flex-wrap items-end gap-3">
              <label className="text-sm">Confidence <input className={`${inp} ml-1 w-20`}
                type="number" step="0.05" min="0.05" max="0.95" value={confidence}
                onChange={e => setConfidence(+e.target.value)} /></label>
              <label className="text-sm" title="Pulls every correlation toward 1 — the crisis-contagion assumption">
                Correlation stress <input className={`${inp} ml-1 w-20`}
                type="number" step="0.1" min="0" max="1" value={corrStrength}
                onChange={e => setCorrStrength(+e.target.value)} /></label>
              <button className={btnPrimary} disabled={scen.isPending} onClick={() => scen.mutate()}>
                {scen.isPending ? <Loader2 className="animate-spin" size={15} /> : <Play size={15} />}
                Run scenario
              </button>
            </div>

            {scen.isPending && <div className="mt-3"><Spinner label="Blending views into the prior…" /></div>}
            {scen.data && !scen.data.available && <div className="mt-3"><Note tone="warn">{scen.data.reason}</Note></div>}

            {scen.data?.available && (
              <>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <Kpi label="Book return change"
                       value={pct(scen.data.summary?.return_change)} />
                  <Kpi label="Book volatility change"
                       value={pct(scen.data.summary?.volatility_change)} />
                </div>
                {!scen.data.has_candidate && fundId && (
                  <div className="mt-3"><Note tone="warn">
                    The candidate was left out: it does not overlap the book by enough
                    months. The scenario below is the book without it.
                  </Note></div>
                )}
                <div className="mt-3 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead><tr>{['Sleeve', 'Weight', 'Prior return', 'Scenario return',
                                 'Prior vol', 'Scenario vol'].map(h =>
                      <th key={h} className={th}>{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {(scen.data.sleeves || []).map((s: string) => {
                        const moved = scen.data.mu?.[s] !== scen.data.prior_mu?.[s]
                        return (
                          <tr key={s} className={moved ? '' : 'text-gray-500'}>
                            <td className={`${td} font-medium`}>{s}</td>
                            <td className={td}>{pct(scen.data.weights?.[s])}</td>
                            <td className={td}>{pct(scen.data.prior_mu?.[s])}</td>
                            <td className={td}>{pct(scen.data.mu?.[s])}</td>
                            <td className={td}>{pct(scen.data.prior_volatility?.[s])}</td>
                            <td className={td}>{pct(scen.data.volatility?.[s])}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Sleeves without a view still move, through their covariance with the
                  ones that have one. {scen.data.note}
                  {scen.data.used_skfolio === false &&
                    ' skfolio is not installed here, so the He-Litterman fallback was used.'}
                </div>
              </>
            )}
          </Panel>
        </div>
      )}
    </div>
  )
}

function Kpi({ label, value }: { label: string; value: any }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-0.5 text-xl font-semibold text-gray-900">{value ?? '—'}</div>
    </div>
  )
}

function Panel({ title, children }: { title: string; children: any }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-2 text-sm font-medium text-gray-700">{title}</div>
      {children}
    </div>
  )
}

function Spinner({ label }: { label?: string }) {
  return <div className="flex items-center gap-2 text-sm text-gray-500">
    <Loader2 className="animate-spin" size={16} />{label || 'Loading…'}</div>
}

function Note({ children, tone = 'info' }: { children: any; tone?: 'info' | 'warn' }) {
  const cls = tone === 'warn'
    ? 'bg-amber-50 text-amber-800 border-amber-200'
    : 'bg-blue-50 text-blue-800 border-blue-200'
  return (
    <div className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${cls}`}>
      {tone === 'warn' && <AlertTriangle size={14} className="mt-0.5 flex-none" />}
      <div>{children}</div>
    </div>
  )
}
